<script setup>
import { ref, onMounted } from 'vue'
import { $bus } from '@/common/events.js'
import { defaultServiceOptions } from '@/common/config'
import { dialog } from '@/common/dialog'
import { $router } from '@/router'
import { useAppState } from '@/composables/app'
import { useAuthState } from '@/composables/auth'
import { useSettings } from '@/composables/settings'
import { useTranslate } from '@/composables/language'
import { LogService } from '@/services/LogService.js'
import { AuthService } from '@/services/AuthService.js'
import { ConfigService } from '@/services/ConfigService.js'

const configService = new ConfigService();
const authService = new AuthService();
const logService = new LogService();

const authStore = useAuthState();
const { t, culture } = useTranslate();

const { appState } = useAppState();

const time = ref("");
const message = ref(null);
let sessionExpiresAt = null;

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  title: {
    type: String,
    default: null
  },
  userDisplayName: {
    type: String,
    default: null
  },
});

function updateTime() {
  let date = new Date();
  //let parts = date.toISOString().replace(/\.\d{3}Z$/, "").split("T");
  //time.value = parts.join(" "); //UTC time
  //time.value = date.toLocaleString("sl-SI", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  if (sessionExpiresAt) {
    let expiresIn = sessionExpiresAt.getTime() - date.getTime();
    if (expiresIn > 0) {
      let hh = Math.floor(expiresIn / 1000 / 3600);
      let mm = Math.floor(expiresIn / 1000 % 3600 / 60);
      let ss = Math.floor(expiresIn / 1000 % 60);
      time.value = (mm < 10 ? "0" : "") + mm + ":" + (ss < 10 ? "0" : "") + ss;
    }
    else {
      time.value = "00:00";
    }
  }
  else {
    time.value = date.toLocaleString(culture.value).replace(/\s+/g, "").replace(",", " ").split(" ")[1]; //Local time
  }
}

setInterval(updateTime, 1000);
updateTime();

onMounted(() => {
  //let now = new Date().getTime();
  //sessionExpiresAt = new Date(now + 15000);
  $bus.on("session warning", data => {
    let now = new Date().getTime();
    sessionExpiresAt = new Date(now + data.expiresIn * 1000);
  });
  $bus.on("session extended", data => {
    sessionExpiresAt = null;
  });
  $bus.on("header message", data => {
    message.value = data.message;
  });
});

async function goToProfile() {
  //$router.push({ name: "UserProfile" }); //TODO: Unlock when editing profile works well
}

async function promptLogout() {
  let result = await dialog({
    type: "question",
    title: t('service.confirm_title'),
    text: t('topbar.logout_confirm'),
    confirmButtonText: t('menu.logout'),
    cancelButtonText: t('generic.cancel'),
  });
  
  if (result && result.confirm) {
    logout();
  }
}

async function logout(e) {
  try {
    let sessionId = authStore.sessionId.value;
    let userId = authStore.userId.value;

    //Clear session
    authStore.logout({ userInitiated: true });

    try {
      //Clear session on the server side
      await authService.logout({ sessionId });
    }
    catch (err) {
      if (appState.isLogEnabled) {
        logService.insertLog({
          a: "user.logout",
          t: "audit",
          s: "info",
          uid: userId,
          sid: sessionId,
          msg: t('topbar.logout_log'),
          //data: result,
          ts: new Date()
        }).then(console.log).catch(console.error);
      }
    }

    configService.clearFlag("session").then(() => { }).catch(() => { });
  }
  catch (err) {
    console.error(err);
  }

  try {
    //Redirect to login page
    $router.push({ name: "Login" });
  }
  catch (err) {
    console.error(err);
  }
}
</script>

<template>
  <div class="w-full bg-white dark:bg-gray-800 h-16 border-b border-gray-200 dark:border-gray-600 flex justify-between">

    <!-- Page title -->
    <div class="grid items-center pl-5">
      <div class="text-md dark:text-white">{{ props.title }}</div>
    </div>

    <!-- Message in the middle -->
    <div class="grid items-center justify-center" v-if="message">
      <div class="text-md text-red-700 dark:text-red-500 animate-blink">{{ message }}</div>
    </div>
    <div class="grid items-center justify-center" v-if="!message">
      <div class="text-md font-mono dark:text-white" v-if="!sessionExpiresAt">{{ time }}</div>
      <div class="text-md font-mono text-red-700 dark:text-red-500 animate-blink" v-if="sessionExpiresAt"><i class="fa fa-regular fa-clock"></i> {{ time }}</div>
    </div>
    <div class="text-right">

      <!-- User name as button with icon -->
      <div class="inline-flex h-16 items-center justify-center" v-if="props.userDisplayName && props.userDisplayName.length">
        <a href="#" class="grid px-5 h-10 w-full place-content-center rounded-lg bg-gray-100 dark:bg-gray-900 text-xs text-gray-600 dark:text-gray-400" @click.prevent="goToProfile">
          <span><i class="fa fa-user mr-2"></i> <span class="text-md">{{ props.userDisplayName }}</span></span>
        </a>
      </div>

      <!-- User name -->
      <!-- <div class="inline-flex text-md">{{ props.userDisplayName }}</div> -->

      <!-- Log out button -->
      <div class="inline-flex h-16 w-16 items-center justify-center mr-2">
        <a href="#" class="grid h-10 w-10 place-content-center rounded-lg bg-gray-100 dark:bg-gray-900 text-xs text-gray-600 dark:text-gray-400" @click.prevent="promptLogout">
          <i class="fa fa-arrow-right-from-bracket"></i>
        </a>
      </div>

    </div>
  </div>
</template>

<style scoped>
.animate-blink {
  animation: blinker 1s step-start infinite;
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}
</style>
